export const WasteBreakdownOptions = [
  { label: 'Waste Category', value: 'mainWasteCategory' },
  { label: 'Material', value: 'material' },
  { label: 'Treatment Method', value: 'treatment' },
  
] as const;

export type WasteBreakdownValue = (typeof WasteBreakdownOptions)[number]['value'];

export type { WastePeriodValue } from 'models/WasteDashboard/wasteGenerated.model';

export type WastePeriodFilter =
  | { type: 'annual' }
  | { type: 'month'; month: number }
  | { type: 'quarter'; quarter: number };

export const toPeriodQueryParams = (
  filter: WastePeriodFilter
): { month?: number; quarter?: number } => {
  if (filter.type === 'month') return { month: filter.month };
  if (filter.type === 'quarter') return { quarter: filter.quarter };
  return {};
};

export const fromAppliedFilters = (
  month?: string,
  quarter?: string
): WastePeriodFilter => {
  if (month) return { type: 'month', month: Number(month) };
  if (quarter) return { type: 'quarter', quarter: Number(quarter) };
  return { type: 'annual' };
};

export const MONTH_OPTIONS = [
  { label: 'Jan', value: '1' },
  { label: 'Feb', value: '2' },
  { label: 'Mar', value: '3' },
  { label: 'Apr', value: '4' },
  { label: 'May', value: '5' },
  { label: 'Jun', value: '6' },
  { label: 'Jul', value: '7' },
  { label: 'Aug', value: '8' },
  { label: 'Sep', value: '9' },
  { label: 'Oct', value: '10' },
  { label: 'Nov', value: '11' },
  { label: 'Dec', value: '12' },
];

export const QUARTER_OPTIONS = [
  { label: 'Q1', value: '1' },
  { label: 'Q2', value: '2' },
  { label: 'Q3', value: '3' },
  { label: 'Q4', value: '4' },
];

export const WasteDashboardText = {
  DASHBOARD: 'Waste Dashboard',
  YOY_TEXT: 'Waste Diversion Rate by Year',
  MATERIAL_TEXT: 'Waste by Material',
  BREAKDOWN_TEXT:
    'Waste Breakdown by',
  KPI_TOTAL_WASTE: 'Total Waste',
  KPI_LANDFILL_WASTE: 'Landfill Waste',
  KPI_DIVERTED_WASTE: 'Diverted Waste',
  KPI_DIVERSION_RATE: 'Diversion Rate',
  DATA_EMPTY: 'Complete Scope 3: Category 5 data entry to view waste details',
  ALL: 'All',
  YEAR: 'Year',
  UNIT_TONNES: 'tonnes',
  UNIT_PERCENT: '%',
  SELECT_TEXT: 'Select',
  SELECT_MONTH: 'Select Month',
  SELECT_QUARTER: 'Select Quarter',
  WARNING_TITLE_YOY_DIVERSION: 'Waste Diversion Rate Info',
  WARNING_TITLE_MATERIAL: 'Waste by Material Info',
  WARNING_TITLE_TREATMENT: 'Waste by Treatment Method Info',
  WARNING_TITLE_MAIN_WASTE_CATEGORY: 'Waste by Waste Category Info',
  WARNING_TITLE_BREAKDOWN: 'Waste Breakdown Info',
  TOOLTIP_MATERIAL: 'Material :',
  TOOLTIP_TREATMENT: 'Treatment Method :',
  TOOLTIP_MAIN_WASTE_CATEGORY: 'Waste Category :',
  TOOLTIP_WASTE: 'Waste :',
};

export const WasteSummaryTableText = {
  TITLE: 'Waste Summary',
  LOCATION_NAME: 'Location Name',
  TOTAL_WASTE: 'Total Waste (MT)',
  TOTAL_LANDFILL: 'Total Landfill (MT)',
  TOTAL_RECYCLE: 'Total Recycle (MT)',
  TOTAL_COMPOSTING: 'Total Waste Composting (MT)',
  TOTAL_DIVERTED: 'Total Waste Diverted (MT)',
  DIVERSION_RATE: 'Waste Diversion Rate',
  EMPTY: 'No waste data available for the selected filters.',
  ERROR_TITLE: 'Waste Summary Info',
  ERROR: 'Unable to load the waste summary.',
  RETRY: 'Retry',
  DOWNLOAD: 'Download Waste Summary',
  XLSX: 'XLSX',
  EXPORT_ERROR: 'Unable to download the waste summary. Please try again.',
  OWNERSHIP_NOTE:
    'Location values are not ownership-weighted and may not sum to the dashboard totals.',
  FILE_PREFIX: 'waste-summary',
  FILE_EXTENSION: '.xlsx',
  DEFAULT_ORG_SLUG: 'organization',
};

export const EMPTY_VALUE = '—';

export type AppliedWasteFilters = {
  organizationId: number;
  organizationName: string;
  year: number;
  month?: string;
  quarter?: string;
  locationId?: number;
};

const quantityFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const formatQuantity = (value?: number | null): string => {
  if (value == null || !Number.isFinite(Number(value))) return EMPTY_VALUE;
  return quantityFormatter.format(Number(value));
};

export const formatDiversionRate = (rate?: number | null): string => {
  if (rate == null) return EMPTY_VALUE;
  const n = Number(rate);
  if (!Number.isFinite(n)) return EMPTY_VALUE;
  return `${n.toFixed(1)}%`;
};

const getMonthLabel = (month: string) =>
  MONTH_OPTIONS.find((option) => option.value === month)?.label ?? month;

const getQuarterLabel = (quarter: string) =>
  QUARTER_OPTIONS.find((option) => option.value === quarter)?.label ??
  `Q${quarter}`;

export const getWastePeriodLabel = (
  year: number,
  month?: string,
  quarter?: string
): string => {
  if (month) return `${getMonthLabel(month)} ${year}`;
  if (quarter) return `${year} · ${getQuarterLabel(quarter)}`;
  return String(year);
};

const toSlug = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const buildWasteSummaryBaseName = ({
  organizationName,
  year,
  month,
  quarter,
}: AppliedWasteFilters): string => {
  const { FILE_PREFIX, DEFAULT_ORG_SLUG } = WasteSummaryTableText;
  const orgSlug = toSlug(organizationName) || DEFAULT_ORG_SLUG;
  const periodSuffix = month
    ? `-${getMonthLabel(month)}`
    : quarter
      ? `-${getQuarterLabel(quarter)}`
      : '';
  return `${FILE_PREFIX}-${orgSlug}-${year}${periodSuffix}`;
};

export const buildWasteSummaryFileName = (
  filters: AppliedWasteFilters
): string =>
  `${buildWasteSummaryBaseName(filters)}${WasteSummaryTableText.FILE_EXTENSION}`;
